import { ChangeDetectionStrategy, Component, Inject, Injector, OnDestroy, OnInit, Optional } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  PageChangeEvent,
  PaginatedSearchHeaderComponentModule,
  SnackbarService,
  TokenPaginatorComponentModule,
} from '@shared/ui';

import { EDITOR_DIALOG_LARGE } from '../../editor-dialog-sizes';
import { ResourceEditorOutcome } from '../../../effects/resource-editor.effects';
import { ResourceData } from '../../../state/resource.state';
// Type-only: erased at runtime. The VALUE import happens dynamically inside
// `openEditorDialog` — a static one would close the module cycle
// manager → dialog → editor → manager (the editor's decorator imports this
// component), leaving someone's decorator with an undefined class at eval time.
import type { ResourceEditorDialogComponent } from '../resource-editor/resource-editor-dialog.component';
import type { ResourceEditorDialogData } from '../resource-editor/resource-editor-dialog.facade';
import { ResourceColumn, ResourceListItem, ResourcesListComponentModule } from '../resources-list.component';
import { ResourceManagerFacade, RESOURCE_MANAGER_NEW_RESOURCE_TYPE_LOCK } from './resource-manager.facade';

/**
 * Smart component that stages assignment of resources to a container. Receives
 * everything through injected state — the container it edits comes from the
 * injected `AssignedResourcesState` (seeded by the consumer) + the
 * `RESOURCE_MANAGER_CONTAINER_ID` token. The NEW RESOURCE button and the
 * right-list edit icons open the resource editor dialog; the dialog session
 * stages its own result, so the manager only opens it.
 */
@Component({
  selector: 'ts-resource-manager',
  templateUrl: './resource-manager.component.html',
  styleUrls: ['./resource-manager.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    MatDialogModule,
    PaginatedSearchHeaderComponentModule,
    TokenPaginatorComponentModule,
    ResourcesListComponentModule,
  ],
})
export class ResourceManagerComponent implements OnInit, OnDestroy {
  public readonly leftColumns = [ResourceColumn.NAME, ResourceColumn.ASSIGNED];
  public readonly rightColumns = [ResourceColumn.NAME, ResourceColumn.EDIT, ResourceColumn.ASSIGNED];

  // Page size is locked at 10
  public readonly pageSizeOptions = [10];

  private readonly destroy$ = new Subject<void>();

  constructor(
    public readonly facade: ResourceManagerFacade,
    private readonly snackbar: SnackbarService,
    private readonly dialog: MatDialog,
    private readonly injector: Injector,
    @Optional()
    @Inject(RESOURCE_MANAGER_NEW_RESOURCE_TYPE_LOCK)
    private readonly newResourceTypeLock: string | null
  ) {}

  ngOnInit(): void {
    // A type-locked manager is scoped to one resource type: the same token that
    // locks the NEW-resource form's type select also restricts this catalog to
    // that type. Set before `initialize` so the single initial load reads it.
    if (this.newResourceTypeLock) this.facade.setTypeFilter(this.newResourceTypeLock);

    this.facade.initialize();

    this.facade.childrenLoadFailed$
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ key, params }) => this.snackbar.show(key, { duration: 0, params }));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public searchTermChanged(value: string): void {
    this.facade.search(value);
  }

  public handlePageEvent(event: PageChangeEvent<ResourceData>): void {
    this.facade.loadMore(event.nextToken);
  }

  public handleAssignedPageEvent(event: PageChangeEvent<unknown>): void {
    this.facade.goToAssignedPage(event.pageIndex);
  }

  public onToggleAvailable(item: ResourceListItem): void {
    this.facade.toggleAvailable(item);
  }

  public onUnassign(item: ResourceListItem): void {
    this.facade.unassign(item);
  }

  public onEditAssigned(item: ResourceListItem): void {
    void this.openEditorDialog(item.id);
  }

  public onNewResource(): void {
    void this.openEditorDialog(null);
  }

  /**
   * UI control lives here (not in the facade): open the editor dialog with this
   * component's injector so the shared tree/ledger reach the dialog's session
   * scope. The dialog component carries its own session providers and its
   * facade stages the result, so there is nothing to do on close. The dynamic
   * import keeps the module graph acyclic (see the type-only imports above).
   */
  private async openEditorDialog(resourceId: string | null): Promise<void> {
    const { ResourceEditorDialogComponent: dialogComponent } = await import(
      '../resource-editor/resource-editor-dialog.component'
    );

    this.dialog.open<ResourceEditorDialogComponent, ResourceEditorDialogData, ResourceEditorOutcome | undefined>(
      dialogComponent,
      {
        ...EDITOR_DIALOG_LARGE,
        hasBackdrop: true,
        injector: this.injector,
        data: {
          resourceId,
          parentId: this.facade.containerId,
          lockType: this.newResourceTypeLock ?? undefined,
        },
      }
    );
  }
}
